import './index.scss';

import React, {useContext, useMemo, useState} from "react";
import PropTypes from 'prop-types';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEllipsis, faCaretDown} from "@fortawesome/free-solid-svg-icons";

import {
	calculateNextIdxForProfile,
	generateUniqueLinkName,
	getCategoryIdxForProfile,
	getFormattedValue,
	getLinksByCategories,
	handleAddCategory,
	padCategories,
	reindexCategoriesForProfiles,
	sortCategories,
	updateCategoryIndices,
	VIRTUAL_ALL_PROFILE_ID,
	verifyParamsUpdateLink
} from '../../tools';
import {deleteCategory, deleteLink, updateCategory, updateLink} from "../../firebaseTools";

import EditCategory from "../../components/EditCategory";
import EditLink from "../../components/EditLink";
import {StateContext} from "../../App";

const Body = ({isEdit, categories, links, activeProfile, addError}) => {
	const {
		profiles,
		allCategories,
		setAllCategories,
		allLinks,
		setAllLinks
	} = useContext(StateContext);

	const [editingCategoryObject, setEditingCategoryObject] = useState(null);
	const [editingLinkObject, setEditingLinkObject] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [collapsed, setCollapsed] = useState({});

	const handleCloseModal = () => {
		setEditingCategoryObject(null);
		setEditingLinkObject(null);
	};

	const refreshCategoryMembership = async (categoryId, linksState) => {
		const category = allCategories.find((item) => item.id === categoryId);
		if (!category) return;

		const linksByCategory = linksState.filter((item) => item.categoryId === categoryId);
		if (linksByCategory.length === 0) {
			await deleteCategory(categoryId, addError);
			setAllCategories((prev) => prev.filter((item) => item.id !== categoryId));
			return;
		}

		const nextProfileIds = Array.from(new Set(
			linksByCategory.flatMap((item) => item.profileIds ?? [])
		)).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);

		const nextIdxPerProfile = {...(category.idxPerProfile ?? {})};
		Object.keys(nextIdxPerProfile).forEach((profileId) => {
			if (!nextProfileIds.includes(profileId)) {
				delete nextIdxPerProfile[profileId];
			}
		});

		nextProfileIds.forEach((profileId) => {
			if (nextIdxPerProfile[profileId] == null) {
				nextIdxPerProfile[profileId] = calculateNextIdxForProfile(allCategories, profileId);
			}
		});

		const updatedCategory = {
			...category,
			profileIds: nextProfileIds,
			idxPerProfile: nextIdxPerProfile,
			idx: getCategoryIdxForProfile(category, activeProfile?.id ?? VIRTUAL_ALL_PROFILE_ID)
		};
		await updateCategory(updatedCategory, addError);
		setAllCategories((prev) => prev.map((item) => item.id === updatedCategory.id ? updatedCategory : item));
	};

	const categoriesToDisplay = useMemo(() =>
			sortCategories(padCategories(categories)),
		[categories]
	);

	const linksByCategory = useMemo(() => {
		if (!links || !Array.isArray(links)) return {};
		return getLinksByCategories(categoriesToDisplay, links);
	}, [links, categoriesToDisplay]);

	const {filteredCategories, filteredLinksByCategory} = useMemo(() => {
		const term = searchTerm.trim().toLowerCase();
		if (term.length < 2) {
			return {
				filteredCategories: categoriesToDisplay,
				filteredLinksByCategory: linksByCategory,
			};
		}

		const matches = (str) =>
			str.toLowerCase()
				.split(/\s+/)
				.some((w) => w.startsWith(term));

		const matchingLinksByCategory = links.reduce((acc, link) => {
			if (!matches(link.name)) {
				return acc;
			}

			if (!acc[link.category]) {
				acc[link.category] = [];
			}
			acc[link.category].push(link);
			return acc;
		}, {});

		const filteredCats = [];
		const filteredLinksCatMap = {};

		for (const cat of categoriesToDisplay) {
			const catKey = cat.category;

			if (matches(catKey)) {
				filteredCats.push(cat);
				filteredLinksCatMap[catKey] = linksByCategory[catKey] || [];
			} else {
				const sub = matchingLinksByCategory[catKey] || [];
				if (sub.length) {
					filteredCats.push(cat);
					filteredLinksCatMap[catKey] = sub;
				}
			}
		}

		return {
			filteredCategories: filteredCats,
			filteredLinksByCategory: filteredLinksCatMap,
		};
	}, [searchTerm, categoriesToDisplay, linksByCategory, links]);

	const handleUpdateLink = async (linkInput) => {
		const link = {
			...linkInput,
			profileIds: Array.isArray(linkInput.profileIds) ? linkInput.profileIds : []
		};
		const downloadUrl = await verifyParamsUpdateLink(link, addError);
		if (downloadUrl === null) {
			handleCloseModal();
			return;
		}

		const linkId = link.id;
		let name = link.name;
		const oldLink = allLinks.find((l) => l.id === linkId);

		const category = getFormattedValue(link.category);
		const categoryId = await handleAddCategory({
			allCategories,
			setAllCategories,
			categories,
			category,
			profileIds: link.profileIds,
			addError
		});

		if (categoryId == null) {
			addError("Failed to add category");
			handleCloseModal();
			return;
		}

		const existing = allLinks.find(item => item.id === linkId);
		if (existing && existing.name !== name) {
			name = generateUniqueLinkName(allLinks, getFormattedValue(link.name), category, false, false, link.profileIds[0]);
		}

		const linkData = {
			categoryId,
			category,
			name,
			url: link.url,
			image: downloadUrl,
			profileIds: link.profileIds
		};

		await updateLink(linkId, linkData, addError);

		const nextLinks = allLinks.map((currentLink) =>
			currentLink.id === linkId ? {...currentLink, ...linkData} : currentLink
		);
		setAllLinks(nextLinks);

		if (oldLink?.categoryId && oldLink.categoryId !== categoryId) {
			await refreshCategoryMembership(oldLink.categoryId, nextLinks);
		}
		await refreshCategoryMembership(categoryId, nextLinks);

		handleCloseModal();
	};

	const handleRemoveLink = async (linkToRemove) => {
		if (!window.confirm(`Are you sure you want to delete "${linkToRemove.name}"?`)) {
			return;
		}

		const profileId = activeProfile?.id;
		const isAll = profileId === VIRTUAL_ALL_PROFILE_ID;
		const existing = allLinks.find((item) => item.id === linkToRemove.id);
		if (!existing) return;

		let nextLinks = [...allLinks];
		if (isAll) {
			const removed = await deleteLink(existing.id, addError);
			if (!removed) return;
			nextLinks = nextLinks.filter((item) => item.id !== existing.id);
		} else {
			const remainingProfileIds = (existing.profileIds ?? []).filter((id) => id !== profileId);
			if (remainingProfileIds.length === 0) {
				const removed = await deleteLink(existing.id, addError);
				if (!removed) return;
				nextLinks = nextLinks.filter((item) => item.id !== existing.id);
			} else {
				const updated = {...existing, profileIds: remainingProfileIds};
				await updateLink(existing.id, {profileIds: remainingProfileIds}, addError);
				nextLinks = nextLinks.map((item) => item.id === existing.id ? updated : item);
			}
		}

		setAllLinks(nextLinks);
		await refreshCategoryMembership(existing.categoryId, nextLinks);
		handleCloseModal();
	};

	const handleRemoveCategory = async (categoryData) => {
		if (!window.confirm(`Are you sure you want to delete the category "${categoryData.category}"?`)) {
			return;
		}

		const categoryId = categoryData?.id;
		if (!categoryId) {
			addError("Invalid category data for removal");
			return;
		}

		const linksInCategory = allLinks.filter((link) => link.categoryId === categoryId);
		const isAll = activeProfile?.id === VIRTUAL_ALL_PROFILE_ID;
		let nextLinks = [...allLinks];

		for (const link of linksInCategory) {
			if (isAll) {
				await deleteLink(link.id, addError);
				nextLinks = nextLinks.filter((item) => item.id !== link.id);
				continue;
			}

			if (!link.profileIds?.includes(activeProfile.id)) {
				continue;
			}

			const remainingProfileIds = link.profileIds.filter((id) => id !== activeProfile.id);
			if (remainingProfileIds.length === 0) {
				await deleteLink(link.id, addError);
				nextLinks = nextLinks.filter((item) => item.id !== link.id);
			} else {
				await updateLink(link.id, {profileIds: remainingProfileIds}, addError);
				nextLinks = nextLinks.map((item) =>
					item.id === link.id ? {...item, profileIds: remainingProfileIds} : item
				);
			}
		}

		setAllLinks(nextLinks);
		await refreshCategoryMembership(categoryId, nextLinks);
		handleCloseModal();
	};

	const handleSaveCategory = async (categoryData) => {
		const category = allCategories.find((item) => item.id === categoryData?.id);
		if (!category) {
			addError("Invalid category data for update");
			return;
		}

		const oldCategoryName = category.category;
		const nextCategoryName = getFormattedValue(String(categoryData.category ?? "").trim());
		const profileId = activeProfile?.id ?? VIRTUAL_ALL_PROFILE_ID;
		const currentIdx = profileId === VIRTUAL_ALL_PROFILE_ID
			? Number(category.idx)
			: Number(getCategoryIdxForProfile(category, profileId));

		let nextCategoriesState = allCategories;
		if (currentIdx !== Number(categoryData.idx)) {
			nextCategoriesState = await updateCategoryIndices(
				allCategories,
				category.id,
				Number(categoryData.idx),
				currentIdx,
				addError,
				profileId
			);
		}

		const reorderedCategory = nextCategoriesState.find((item) => item.id === category.id) ?? category;
		const normalizedNextName = nextCategoryName.toLowerCase();
		const targetCategory = nextCategoriesState.find((item) =>
			item.id !== reorderedCategory.id
			&& String(item.category ?? "").trim().toLowerCase() === normalizedNextName
		);

		if (oldCategoryName !== nextCategoryName && targetCategory) {
			const sourceCategory = reorderedCategory;
			const mergedProfileIds = Array.from(new Set([
				...(targetCategory.profileIds ?? []),
				...(sourceCategory.profileIds ?? [])
			])).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);

			const nextIdxPerProfile = {...(targetCategory.idxPerProfile ?? {})};
			for (const mergedProfileId of mergedProfileIds) {
				const sourceIdx = Number(getCategoryIdxForProfile(sourceCategory, mergedProfileId));
				const targetIdx = Number(getCategoryIdxForProfile(targetCategory, mergedProfileId));
				const hasSource = Number.isFinite(sourceIdx);
				const hasTarget = Number.isFinite(targetIdx);

				if (hasSource && hasTarget) {
					nextIdxPerProfile[mergedProfileId] = Math.min(sourceIdx, targetIdx);
				} else if (hasSource) {
					nextIdxPerProfile[mergedProfileId] = sourceIdx;
				} else if (hasTarget) {
					nextIdxPerProfile[mergedProfileId] = targetIdx;
				}
			}

			const mergedCategory = {
				...targetCategory,
				category: nextCategoryName,
				profileIds: mergedProfileIds,
				idxPerProfile: nextIdxPerProfile,
				idx: (() => {
					const idxValues = Object.values(nextIdxPerProfile)
						.map((value) => Number(value))
						.filter(Number.isFinite);
					return idxValues.length > 0 ? Math.min(...idxValues) : Number(targetCategory.idx);
				})()
			};

			await updateCategory(mergedCategory, addError);
			let mergedCategoriesState = nextCategoriesState
				.map((item) => item.id === mergedCategory.id ? mergedCategory : item)
				.filter((item) => item.id !== sourceCategory.id);

			const linksToMove = allLinks.filter((item) => item.categoryId === sourceCategory.id);
			let nextLinksState = [...allLinks];
			for (const link of linksToMove) {
				const nextName = generateUniqueLinkName(
					nextLinksState.filter((item) => item.id !== link.id),
					getFormattedValue(link.name),
					nextCategoryName,
					true,
					true,
					link.profileIds?.[0]
				);
				const migratedLink = {
					...link,
					categoryId: mergedCategory.id,
					category: nextCategoryName,
					name: nextName
				};
				await updateLink(link.id, {
					categoryId: mergedCategory.id,
					category: nextCategoryName,
					name: nextName
				}, addError);
				nextLinksState = nextLinksState.map((item) => item.id === link.id ? migratedLink : item);
			}

			await deleteCategory(sourceCategory.id, addError);
			const affectedProfiles = Array.from(new Set([
				...(sourceCategory.profileIds ?? []),
				...(mergedCategory.profileIds ?? [])
			])).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);
			mergedCategoriesState = await reindexCategoriesForProfiles(mergedCategoriesState, affectedProfiles, addError);

			setAllCategories(mergedCategoriesState);
			setAllLinks(nextLinksState);
			handleCloseModal();
			return;
		}

		const renamedCategory = {
			...reorderedCategory,
			category: nextCategoryName,
			profileIds: (reorderedCategory.profileIds ?? []).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID),
		};

		if (oldCategoryName !== nextCategoryName) {
			await updateCategory(renamedCategory, addError);
			nextCategoriesState = nextCategoriesState.map((item) =>
				item.id === renamedCategory.id ? renamedCategory : item
			);
		}

		setAllCategories(nextCategoriesState);

		if (oldCategoryName !== nextCategoryName) {
			const linksToRename = allLinks.filter((item) => item.categoryId === category.id);
			const renamedLinks = [];
			for (const link of linksToRename) {
				const nextName = generateUniqueLinkName(
					allLinks.filter((item) => item.id !== link.id),
					getFormattedValue(link.name),
					nextCategoryName,
					false,
					true,
					link.profileIds?.[0]
				);
				const nextLink = {...link, category: nextCategoryName, name: nextName};
				await updateLink(link.id, {category: nextCategoryName, name: nextName}, addError);
				renamedLinks.push(nextLink);
			}

			if (renamedLinks.length) {
				setAllLinks((prev) =>
					prev.map((link) => renamedLinks.find((item) => item.id === link.id) ?? link)
				);
			}
		}

		handleCloseModal();
	};

	return (
		<>
			<div className="grid-wrapper">
				<div className="grid-header">
					<input className="search-input"
					       type="text"
					       value={searchTerm}
					       onChange={(e) => setSearchTerm(e.target.value)}
					       placeholder=""
					/>
				</div>
				<div className="categories-grid">
					{filteredCategories.map((categoryObj, index) => (
						<div
							key={categoryObj.id ?? `empty-category-${index}`}
							className={`category-container ${categoryObj.category === "" ? "empty-category" : ""}`}
						>
							{categoryObj.category && (
								<>
									<div className="category-header">
										<h2 className="category-title">{categoryObj.category}</h2>
										{isEdit ?
											<FontAwesomeIcon icon={faEllipsis}
											                 className="category-icon"
											                 onClick={(e) => {
												                 e.preventDefault();
												                 setEditingCategoryObject(categoryObj);
											                 }}/>
											:
											<FontAwesomeIcon icon={faCaretDown}
											                 className="collapse-icon"
											                 onClick={() => {
												                 setCollapsed(prev => ({
													                 ...prev,
													                 [categoryObj.category]: !prev[categoryObj.category]
												                 }));
											                 }}/>
										}
									</div>
									{(
										!collapsed[categoryObj.category]
											? filteredLinksByCategory[categoryObj.category] || []
											: []
									).map(link => (
										<div className="link-container"
										     key={link.id}
										     onClick={(e) => {
											     if (isEdit) {
												     e.preventDefault();
												     setEditingLinkObject(link);
											     }
										     }}>
											<a className="link" rel="noopener noreferrer" href={link.url} target="_blank">
												<img src={link.image} className="link-image" alt=""/>
												<span className="link-text">{link.name}</span>
											</a>
										</div>
									))}
								</>
							)}
						</div>
					))}
				</div>
			</div>
			{editingCategoryObject && (
				<EditCategory
					categories={categories}
					categoryObject={editingCategoryObject}
					onClose={handleCloseModal}
					onSave={handleSaveCategory}
					onRemove={handleRemoveCategory}
				/>
			)}

			{editingLinkObject && (
				<EditLink
					links={allLinks}
					categories={categories}
					profiles={profiles}
					link={editingLinkObject}
					onClose={handleCloseModal}
					onSave={handleUpdateLink}
					onRemove={handleRemoveLink}
				/>
			)}
		</>
	);
};

Body.propTypes = {
	isEdit: PropTypes.bool.isRequired,
	categories: PropTypes.array.isRequired,
	links: PropTypes.array.isRequired,
	activeProfile: PropTypes.shape({
		id: PropTypes.string
	}),
	addError: PropTypes.func.isRequired,
};

export default Body;
